import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingCart, FlaskConical, CreditCard, Loader2, CheckCircle2, XCircle, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, mediaSrc, money, DELIVERY_LABEL } from "@/lib/api";
import { EmptyState } from "@/components/vaulted";
import { CryptoCheckout } from "@/pages/Crypto";

export function CartPage() {
  const { items, setQty, remove, replace, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [codes, setCodes] = useState([]);
  const [quote, setQuote] = useState(null);
  const [crypto, setCrypto] = useState(null);

  useEffect(() => {
    if (!items.length) return setQuote(null);
    api.post("/checkout/quote", { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), codes })
      .then((r) => {
        setQuote(r.data);
        const bad = codes.filter((c) => r.data.errors?.[c]);
        if (bad.length) { toast.error(r.data.errors[bad[0]]); setCodes(codes.filter((c) => !bad.includes(c))); }
      }).catch((e) => toast.error(errMsg(e)));
  }, [items, codes, user]); // eslint-disable-line

  useEffect(() => { api.get("/checkout/config").then((r) => setCfg(r.data)); api.get("/crypto/config").then((r) => setCrypto(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!items.length) return;
    api.post("/cart/validate", items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }))).then((r) => {
      const live = Object.fromEntries(r.data.map((p) => [p.id, p]));
      const next = items.filter((i) => live[i.product_id]).map((i) => ({ ...i, price: live[i.product_id].price, title: live[i.product_id].title }));
      if (next.length !== items.length) toast.message("Some items are no longer available and were removed");
      if (JSON.stringify(next) !== JSON.stringify(items)) replace(next);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const checkout = async (method, coin) => {
    if (!user) return nav("/login?next=/cart");
    setBusy(method);
    try {
      const { data } = await api.post("/checkout", { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), origin_url: window.location.origin, method, coin, codes });
      if (method === "crypto") nav(`/payment/crypto/${data.order_id}`);
      else if (method === "test") { clear(); toast.success("Test order completed"); nav(`/account/orders/${data.order_id}`); }
      else window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  if (!items.length) return (
    <div className="container-v py-16" data-testid="cart-page">
      <EmptyState icon={ShoppingCart} title="Your cart is empty" body="Browse the marketplace to find resources and tools." testId="cart-empty"
        action={<Link to="/marketplace" className="v-btn-primary">Explore Marketplace</Link>} />
    </div>
  );

  return (
    <div className="container-v py-10" data-testid="cart-page">
      <h1 className="text-2xl font-semibold tracking-tight text-white">Cart</h1>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="v-panel divide-y divide-[#27272A]">
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-4 p-4" data-testid={`cart-item-${i.product_id}`}>
              <Link to={`/product/${i.product_id}`} className="aspect-video w-28 shrink-0 overflow-hidden rounded-md border border-[#27272A] bg-[#18181B]">
                {i.thumbnail && <img src={mediaSrc(i.thumbnail)} alt="" className="h-full w-full object-cover" />}
              </Link>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">{i.title}</p>
                <p className="text-xs text-zinc-500">{i.seller} · {DELIVERY_LABEL[i.delivery_method]}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center rounded-md border border-[#27272A]">
                    <button data-testid={`cart-qty-dec-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity - 1)} className="grid h-8 w-8 place-items-center text-zinc-400 hover:text-white"><Minus className="h-3.5 w-3.5" /></button>
                    <span data-testid={`cart-qty-${i.product_id}`} className="w-8 text-center text-sm">{i.quantity}</span>
                    <button data-testid={`cart-qty-inc-${i.product_id}`} onClick={() => setQty(i.product_id, i.quantity + 1)} className="grid h-8 w-8 place-items-center text-zinc-400 hover:text-white"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" />Remove</button>
                </div>
              </div>
              <p className="text-sm font-semibold text-white">{money(i.price * i.quantity)}</p>
            </div>
          ))}
        </div>
        <aside className="v-panel h-fit p-5">
          <p className="text-sm font-semibold text-white">Order summary</p>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span data-testid="cart-subtotal">{money(quote ? quote.subtotal : total)}</span></div>
            {quote?.discounts_applied?.map((d) => (
              <div key={d.id} className="flex justify-between text-emerald-400" data-testid={`discount-line-${d.code || "auto"}`}>
                <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{d.code || d.name}{d.automatic && <span className="text-[10px] text-zinc-500">auto</span>}
                  {d.code && <button onClick={() => setCodes(codes.filter((c) => c !== d.code))} className="text-zinc-500 hover:text-white" data-testid="remove-discount"><X className="h-3 w-3" /></button>}</span>
                <span>−{money(d.amount)}</span></div>
            ))}
            <div className="flex justify-between text-zinc-400"><span>Taxes</span><span className="text-xs">Calculated at checkout</span></div>
            <div className="flex justify-between border-t border-[#27272A] pt-3 text-base font-semibold text-white"><span>Total</span><span data-testid="cart-total">{money(quote ? quote.total : total)}</span></div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); const c = codeInput.trim().toUpperCase(); if (c) { setCodes([c]); setCodeInput(""); } }} className="mt-4 flex gap-2">
            <input data-testid="coupon-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Discount code" className="v-input h-9 uppercase" />
            <button data-testid="coupon-apply-button" className="v-btn-ghost h-9 px-3">Apply</button>
          </form>
          {cfg?.stripe_enabled && (
            <button data-testid="checkout-stripe-button" disabled={!!busy} onClick={() => checkout("stripe")} className="mt-5 v-btn-primary w-full h-11">
              {busy === "stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Checkout with Stripe
            </button>
          )}
          {cfg?.stripe_mode === "test" && <p className="mt-2 text-[11px] text-amber-300/80">Stripe is in test mode — no real charges. Use card 4242 4242 4242 4242.</p>}
          {crypto?.enabled && <CryptoCheckout cfg={crypto} busy={busy} onPay={(coin) => checkout("crypto", coin)} />}
          {cfg?.test_checkout_enabled && (
            <div className="mt-4 rounded-md border border-dashed border-amber-500/30 bg-amber-500/5 p-3">
              <p className="flex items-center gap-1.5 text-xs font-medium text-amber-300"><FlaskConical className="h-3.5 w-3.5" />Development test checkout</p>
              <p className="mt-1 text-[11px] text-zinc-400">Simulates a verified payment without processing money. Orders are flagged as test and excluded from revenue.</p>
              <button data-testid="checkout-test-button" disabled={!!busy} onClick={() => checkout("test")} className="mt-2 v-btn-ghost v-btn-sm w-full">
                {busy === "test" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Place test order
              </button>
            </div>
          )}
          {!user && <p className="mt-3 text-xs text-zinc-500">You'll be asked to log in before checkout.</p>}
        </aside>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking" });
  const tries = useRef(0);
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState({ status: "error" });
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { clear(); return setState({ status: "paid", order_id: data.order_id }); }
        if (data.status === "expired") return setState({ status: "expired" });
      } catch { return setState({ status: "error" }); }
      if (++tries.current < 15) setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
  }, []); // eslint-disable-line
  return (
    <div className="container-v py-20 max-w-lg" data-testid="payment-success-page">
      <div className="v-panel p-8 text-center">
        {state.status === "checking" && <><Loader2 className="mx-auto h-8 w-8 animate-spin text-violet-400" /><p className="mt-4 text-white">Confirming your payment…</p></>}
        {state.status === "paid" && <><CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" /><p data-testid="payment-confirmed" className="mt-4 text-lg font-semibold text-white">Payment confirmed</p>
          <p className="mt-1 text-sm text-zinc-400">Your order is ready in your dashboard.</p><Link to={`/account/orders/${state.order_id}`} data-testid="payment-view-order" className="mt-6 v-btn-primary">View order</Link></>}
        {["expired", "error", "timeout"].includes(state.status) && <><XCircle className="mx-auto h-10 w-10 text-red-400" /><p className="mt-4 text-white">{state.status === "timeout" ? "Still processing — check your orders shortly." : "We couldn't confirm this payment."}</p>
          <Link to="/account/orders" className="mt-6 v-btn-ghost">My orders</Link></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="container-v py-20 max-w-lg" data-testid="payment-cancel-page">
      <div className="v-panel p-8 text-center">
        <XCircle className="mx-auto h-10 w-10 text-zinc-500" />
        <p className="mt-4 text-lg font-semibold text-white">Checkout cancelled</p>
        <p className="mt-1 text-sm text-zinc-400">No payment was taken. Your cart is still saved.</p>
        <Link to="/cart" className="mt-6 v-btn-primary">Return to cart</Link>
      </div>
    </div>
  );
}
